import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import {
  ArrowTurnUprightOutlinedIcon,
  ChartLineIcon,
  CheckIcon,
  EnterIcon,
  FolderIcon,
  GoogleClassroomLogoIcon,
  PenPaperIcon,
  SquareStackIcon,
  TrashIcon,
} from '@/icons';
import type ZepIcon from '@/icons/ZepIcon';
import { useExternalPlatform } from '@/shared/externalPlatform';
import { FOLDER_COPY } from '@/shared/features/quiz-folder';
import { type QuizListType } from '@/shared/quizList/QuizListType';
import { type CommonTranslationKeys } from '@/types/translation';
import type { ButtonProps } from '@/ui_v3';

const CommonQuizMenuTypes: QuizListType[] = ['public', 'featured', 'user'];

/**
 * 남의 폴더 안 퀴즈 행 (Figma 35044:67158).
 *
 * 이 화면에는 폴더 전체를 재생하는 CTA 가 없다 — 체험은 3문제에서 잠겨 폴더를 이어 풀 수
 * 없기 때문이다. 대신 마음에 드는 퀴즈를 한 개씩 가져가게 두고, 그 자리가 이 버튼이다.
 * `excludeHoverMenu` 를 주지 않아 데스크톱에서는 행 호버 칩, 모바일에서는 케밥 메뉴로 뜬다.
 */
const FolderQuizMenuItems: QuizCardMenuItem[] = [
  {
    // 로케일 시트 등록 전이라 문구는 도메인 상수를 쓴다.
    label: 'detail.copyToMyQuiz',
    rawLabel: FOLDER_COPY.detailSaveQuiz,
    icon: SquareStackIcon,
    variant: 'outlined',
    intent: 'neutral',
    onClickEventName: 'save_to_my_quiz',
    showGoogleClassButton: false,
  },
];

export const useGetQuizListItemButtons = (type: QuizListType) => {
  const { isInGoogleClassroom: inGoogleClassRoom, isInLti: inLti } =
    useExternalPlatform();

  return (quizSet: SearchQuizsetRes) => {
    if (inLti) {
      const ltiMenuButtons =
        type === 'myQuiz' ? ltiMyMenuButtons : [LtiSelectMenuItem];

      return quizSet.isLocked
        ? ltiMenuButtons.filter(item => item.onClickEventName !== 'lti_select')
        : ltiMenuButtons;
    }

    if (CommonQuizMenuTypes.includes(type)) {
      return [];
    }

    /*
      `folder` 는 **남이 보는** 폴더 상세다(소유자는 `myQuiz` 메뉴를 받는다).
      자기 퀴즈가 남의 폴더에 담겨 있을 수 있어 복사를 막지 않는다 — 이 행만은 작성자를 보고
      intent 를 정한다(`useQuizListItemAction` 의 `save_to_my_quiz`).
    */
    if (type === 'folder') {
      return FolderQuizMenuItems;
    }

    const googleMyMenuButtons: QuizCardMenuItem[] = [
      {
        label: 'googleClassroom.createAssignmentButton.text',
        icon: GoogleClassroomLogoIcon,
        intent: 'neutral',
        onClickEventName: 'google_assignment',
      },
    ];

    return inGoogleClassRoom
      ? googleMyMenuButtons
      : MyQuizCardMenuItems.filter(
          d => d.showGoogleClassButton === inGoogleClassRoom,
        ).map(d => ({
          ...d,
          disabled: d.onClickEventName === 'enter' ? quizSet.isLocked : false,
        }));
  };
};

export const LtiSelectMenuItem: QuizCardMenuItem = {
  label: 'moodle.selectQuizButton.text',
  icon: CheckIcon,
  intent: 'neutral',
  onClickEventName: 'lti_select',
};

export const MyQuizEditMenuItem: QuizCardMenuItem = {
  label: 'global.edit.button',
  icon: PenPaperIcon,
  onClickEventName: 'update',
  excludeHoverMenu: true,
  showGoogleClassButton: false,
};

const ltiMyMenuButtons: QuizCardMenuItem[] = [
  MyQuizEditMenuItem,
  LtiSelectMenuItem,
];

export const MyQuizCardMenuItems: QuizCardMenuItem[] = [
  {
    label: 'global.enter.button',
    icon: EnterIcon,
    onClickEventName: 'enter',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.share.button',
    icon: ArrowTurnUprightOutlinedIcon,
    onClickEventName: 'share',
    intent: 'neutral',
    variant: 'contained',
    showGoogleClassButton: false,
  },
  {
    label: 'global.duplicate.button',
    icon: SquareStackIcon,
    variant: 'contained',
    intent: 'neutral',
    onClickEventName: 'copy',
    showGoogleClassButton: false,
  },
  MyQuizEditMenuItem,
  /**
   * 폴더로 이동. 퀴즈는 최대 한 폴더에만 속하므로 "담기"가 아니라 **이동**이다 —
   * 대상 폴더를 고르면 기존 폴더에서 빠지고 대상 맨 뒤에 붙는다.
   */
  {
    // 로케일 시트 등록 전이라 문구는 도메인 상수를 쓴다.
    label: 'global.duplicate.button',
    rawLabel: FOLDER_COPY.moveToFolder,
    icon: FolderIcon,
    variant: 'contained',
    intent: 'neutral',
    onClickEventName: 'move_to_folder',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'lmsBtn',
    icon: ChartLineIcon,
    onClickEventName: 'report',
    variant: 'outlined',
    intent: 'alternative',
    hidePortableDevice: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.delete',
    icon: TrashIcon,
    onClickEventName: 'delete',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
];

export type QuizCardMenuItem = {
  icon: ReturnType<typeof ZepIcon>;
  label: CommonTranslationKeys;
  /**
   * i18n 키 대신 그대로 쓸 문구. 폴더 기능처럼 아직 로케일 시트에 키가 없는 항목용이다
   * (시트 등록 후 `label` 로 옮긴다).
   */
  rawLabel?: string;
  variant?: ButtonProps['variant'];
  intent?: ButtonProps['intent'];
  disabled?: boolean;
  onClickEventName:
    | 'share'
    | 'copy'
    | 'report'
    | 'enter'
    | 'update'
    | 'delete'
    | 'move_to_folder'
    /** 남의 퀴즈를 내 쪽으로 복사한다. `copy` 와 달리 빌드 페이지를 거치지 않는다. */
    | 'save_to_my_quiz'
    | 'google_assignment'
    | 'lti_select';
  excludeHoverMenu?: boolean;
  hidePortableDevice?: boolean;
  showGoogleClassButton?: boolean;
};
